/**
 * ScheduleFollowupModal — R3
 * Opens from InvoiceDetailPage when invoice is sent/partial/overdue.
 * Supports: single follow-up, multi-installment repeat, reminder-only (no amount).
 */
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useCreateFollowup, type FollowupCreate } from '@/hooks/useCollectionFollowups';
import { todayLocalDateString } from '@/lib/localDate';

interface ScheduleFollowupModalProps {
  open: boolean;
  invoiceId: string;
  onOpenChange: (open: boolean) => void;
}

/** Safely add N months to a date string, clamping to last day of target month. */
function addMonthsSafe(dateStr: string, months: number): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const targetMonth = month - 1 + months; // 0-based
  const targetYear = year + Math.floor(targetMonth / 12);
  const targetMonthMod = ((targetMonth % 12) + 12) % 12;
  // Last day of target month
  const lastDay = new Date(targetYear, targetMonthMod + 1, 0).getDate();
  const clampedDay = Math.min(day, lastDay);
  return [
    String(targetYear),
    String(targetMonthMod + 1).padStart(2, '0'),
    String(clampedDay).padStart(2, '0'),
  ].join('-');
}

export function ScheduleFollowupModal({
  open,
  invoiceId,
  onOpenChange,
}: ScheduleFollowupModalProps) {
  const today = todayLocalDateString();

  const [dueDate, setDueDate] = useState(today);
  const [amountStr, setAmountStr] = useState('');
  const [note, setNote] = useState('');
  const [reminderOnly, setReminderOnly] = useState(false);
  const [repeatMonthly, setRepeatMonthly] = useState(false);
  const [monthCount, setMonthCount] = useState(3);
  const [errors, setErrors] = useState<{ dueDate?: string; amount?: string; monthCount?: string }>({});

  const createFollowup = useCreateFollowup(invoiceId);

  // Reset on open
  useEffect(() => {
    if (open) {
      setDueDate(today);
      setAmountStr('');
      setNote('');
      setReminderOnly(false);
      setRepeatMonthly(false);
      setMonthCount(3);
      setErrors({});
    }
  }, [open]);

  const validate = () => {
    const errs: typeof errors = {};
    if (!dueDate) errs.dueDate = 'Due date is required';
    if (!reminderOnly) {
      const num = parseFloat(amountStr);
      if (!amountStr || isNaN(num) || num <= 0) {
        errs.amount = 'Amount must be greater than zero (or check "Reminder only")';
      }
    }
    if (repeatMonthly && (monthCount < 2 || monthCount > 24)) {
      errs.monthCount = 'Month count must be between 2 and 24';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    const amountCents = reminderOnly ? null : Math.round(parseFloat(amountStr) * 100);

    if (repeatMonthly) {
      // Generate N installment rows with a shared group_id
      const groupId = crypto.randomUUID();
      const items: FollowupCreate[] = Array.from({ length: monthCount }, (_, i) => ({
        due_date: addMonthsSafe(dueDate, i),
        expected_amount_cents: amountCents,
        note: note || undefined,
        installment_group_id: groupId,
      }));
      createFollowup.mutate(items, {
        onSuccess: () => onOpenChange(false),
      });
    } else {
      const item: FollowupCreate = {
        due_date: dueDate,
        expected_amount_cents: amountCents,
        note: note || undefined,
      };
      createFollowup.mutate(item, {
        onSuccess: () => onOpenChange(false),
      });
    }
  };

  const formatPreviewDates = () => {
    if (!repeatMonthly || !dueDate || monthCount < 2) return null;
    return Array.from({ length: Math.min(monthCount, 4) }, (_, i) => {
      const d = addMonthsSafe(dueDate, i);
      return new Date(d + 'T00:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
    });
  };

  const previewDates = formatPreviewDates();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="schedule-followup-modal">
        <DialogHeader>
          <DialogTitle>Schedule Follow-up</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Due Date */}
          <div className="space-y-1">
            <Label htmlFor="followup-due-date">Follow-up Date</Label>
            <input
              id="followup-due-date"
              type="date"
              value={dueDate}
              onChange={(e) => {
                setDueDate(e.target.value);
                setErrors((prev) => ({ ...prev, dueDate: undefined }));
              }}
              min={today}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
              data-testid="followup-due-date-input"
            />
            {errors.dueDate && (
              <p className="text-xs text-destructive">{errors.dueDate}</p>
            )}
          </div>

          {/* Reminder Only toggle */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="reminder-only"
              checked={reminderOnly}
              onCheckedChange={(checked) => {
                setReminderOnly(!!checked);
                setErrors((prev) => ({ ...prev, amount: undefined }));
              }}
              data-testid="reminder-only-checkbox"
            />
            <Label htmlFor="reminder-only" className="text-sm cursor-pointer">
              Reminder only (no specific amount)
            </Label>
          </div>

          {/* Amount — hidden when reminder-only */}
          {!reminderOnly && (
            <div className="space-y-1">
              <Label htmlFor="followup-amount">Amount to Collect</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                <Input
                  id="followup-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={amountStr}
                  onChange={(e) => {
                    setAmountStr(e.target.value);
                    setErrors((prev) => ({ ...prev, amount: undefined }));
                  }}
                  placeholder="0.00"
                  className="pl-7"
                  data-testid="followup-amount-input"
                />
              </div>
              {errors.amount && (
                <p className="text-xs text-destructive">{errors.amount}</p>
              )}
            </div>
          )}

          {/* Note */}
          <div className="space-y-1">
            <Label htmlFor="followup-note">Note (optional)</Label>
            <textarea
              id="followup-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. 3rd of 3 payments — e-transfer"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              data-testid="followup-note-input"
            />
          </div>

          {/* Repeat Monthly toggle */}
          <div className="border-t pt-3 space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="repeat-monthly"
                checked={repeatMonthly}
                onCheckedChange={(checked) => {
                  setRepeatMonthly(!!checked);
                  setErrors((prev) => ({ ...prev, monthCount: undefined }));
                }}
                data-testid="repeat-monthly-checkbox"
              />
              <Label htmlFor="repeat-monthly" className="text-sm cursor-pointer">
                Repeat monthly for multiple installments
              </Label>
            </div>

            {repeatMonthly && (
              <div className="pl-6 space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="month-count" className="text-sm whitespace-nowrap">
                    Number of months:
                  </Label>
                  <Input
                    id="month-count"
                    type="number"
                    min="2"
                    max="24"
                    value={monthCount}
                    onChange={(e) => {
                      setMonthCount(parseInt(e.target.value) || 2);
                      setErrors((prev) => ({ ...prev, monthCount: undefined }));
                    }}
                    className="w-20"
                    data-testid="month-count-input"
                  />
                </div>
                {errors.monthCount && (
                  <p className="text-xs text-destructive">{errors.monthCount}</p>
                )}
                {previewDates && (
                  <p className="text-xs text-muted-foreground">
                    Dates: {previewDates.join(', ')}
                    {monthCount > 4 && ` … (${monthCount - 4} more)`}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createFollowup.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={createFollowup.isPending}
            data-testid="save-followup-button"
          >
            {createFollowup.isPending
              ? 'Saving…'
              : repeatMonthly
              ? `Create ${monthCount} Follow-ups`
              : 'Schedule Follow-up'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
